import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  LinearProgress,
  Paper,
  useTheme
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { processingService } from '../../../services/processing';
import { useAppDispatch } from '../../../store';
import { setBackgroundProcessingStatus, setPendingNotification } from '../../../store/slices/processingSlice';
import { apiSlice } from '../../../store/api/apiSlice';
import type { DynamicField, CaseProcessingRequest, ProcessingStatus } from '../../../types';

interface AddCaseModalProps {
  open: boolean;
  onClose: () => void;
  fieldId: string;
  field?: DynamicField;
  onProcessingStarted: (processingId: string) => void;
  onReviewRequired: (processingId: string) => void;
  onError: (error: string) => void;
}

const defaultPromptInstructions = "Pay attention to the specific clinical presentation and patient characteristics. Consider the educational objectives and ensure the case provides realistic learning opportunities for students.";

const rawContentPlaceholder = "Enter the complete case description including patient demographics, presenting symptoms, medical history, medications, and any relevant clinical details. The more detailed the description, the better the LLM extraction will be.";

export const AddCaseModal: React.FC<AddCaseModalProps> = ({
  open,
  onClose,
  fieldId,
  field,
  onProcessingStarted,
  onReviewRequired,
  onError,
}) => {
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<CaseProcessingRequest>({
    fieldId: '',
    rawContent: '',
    promptInstructions: defaultPromptInstructions
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
  const [currentProcessingId, setCurrentProcessingId] = useState<string | null>(null);
  const theme = useTheme();

  // Reset form when modal opens or fieldId changes
  useEffect(() => {
    if (open && fieldId) {
      setFormData({
        fieldId,
        rawContent: '',
        promptInstructions: field?.prompt_instructions || defaultPromptInstructions
      });
      setErrors({});
      setIsProcessing(false);
      setProcessingStatus(null);
      setCurrentProcessingId(null);
    }
  }, [open, fieldId, field]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.rawContent.trim()) {
      newErrors.rawContent = 'Case content is required';
    } else if (formData.rawContent.length < 50) {
      newErrors.rawContent = 'Case content must be at least 50 characters';
    } else if (formData.rawContent.length > 20000) {
      newErrors.rawContent = 'Case content cannot exceed 20,000 characters';
    }

    if (formData.promptInstructions && formData.promptInstructions.length > 4000) {
      newErrors.promptInstructions = 'Prompt instructions cannot exceed 4,000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double submission
    if (isProcessing) {
      console.log('Already processing, ignoring duplicate submission');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setIsProcessing(true);
      
      // Use ONLY the processing service (which handles the API call internally)
      const monitoringResult = await processingService.processCase(formData, {
        onProgress: (status) => {
          console.log('Processing progress:', status);
          setProcessingStatus(status);
          dispatch(setBackgroundProcessingStatus(status));
        },
        onError: (error) => {
          console.error('Processing error:', error);
          setIsProcessing(false);
          onError(error.message);
        }
      });

      // Set processing ID from the result (available immediately)
      setCurrentProcessingId(monitoringResult.processingId);

      if (monitoringResult.success) {
        setIsProcessing(false);
        dispatch(setBackgroundProcessingStatus(null));
        dispatch(apiSlice.util.invalidateTags([{ type: 'Case', id: fieldId }]));
        dispatch(setPendingNotification({ message: 'Case processed successfully! It is now available in the case grid.', severity: 'success' }));
        onProcessingStarted(monitoringResult.processingId);
      } else if (monitoringResult.requiresReview) {
        setIsProcessing(false);
        dispatch(setBackgroundProcessingStatus(null));
        dispatch(apiSlice.util.invalidateTags([{ type: 'Case', id: fieldId }]));
        dispatch(setPendingNotification({ message: 'Case created but flagged for manual review. It will appear in the case grid — look for the review status indicator.', severity: 'warning' }));
        onReviewRequired(monitoringResult.processingId);
      } else {
        setIsProcessing(false);
        dispatch(setBackgroundProcessingStatus(null));
        onError(monitoringResult.error || 'Processing failed');
      }

    } catch (error: any) {
      console.error('Failed to start case processing:', error);
      setIsProcessing(false);
      dispatch(setBackgroundProcessingStatus(null));
      onError(error.message || 'Failed to start processing');
    }
  };

  const handleChange = (field: keyof CaseProcessingRequest) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    } else {
      // If processing, user can close modal but processing continues
      onClose();
    }
  };

  const getProgressPercentage = () => {
    return processingStatus?.progress || 0;
  };

  const getCurrentStep = () => {
    return processingStatus?.currentStep || 'Initializing...';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          Add New Case via LLM Processing
        </Typography>
        <IconButton 
          onClick={handleClose}
          sx={{ color: 'text.primary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.primary', mb: 3 }}>
            Process raw case text using AI to automatically extract case details, patient information, and create a structured medical case.
          </Typography>

          {field && (
            <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Target Field: {field.field_name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {field.description}
              </Typography>
            </Paper>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Raw Content */}
            <Typography variant="body2" >
              Raw Case Content
            </Typography>
            <TextField
              value={formData.rawContent}
              onChange={handleChange('rawContent')}
              error={!!errors.rawContent}
              helperText={errors.rawContent || `${formData.rawContent.length}/20,000 characters`}
              required
              fullWidth
              multiline
              rows={8}
              disabled={isProcessing}
              placeholder={rawContentPlaceholder}
              sx={{
                '& .MuiInputBase-input': {
                  color: theme.palette.text.primary, // or any color like '#999' or 'error.main'
                  fontSize: '0.875rem',
                },
              }}
            />

            {/* Prompt Instructions */}
            <Typography variant="body2">
              AI Instructions
            </Typography>
            <TextField
              value={formData.promptInstructions}
              onChange={handleChange('promptInstructions')}
              error={!!errors.promptInstructions}
              helperText={errors.promptInstructions || 'Special instructions for the AI when responding to the medical provider'}
              fullWidth
              multiline
              rows={3}
              disabled={isProcessing}
              placeholder="Enter specific processing instructions..."
            />
          </Box>

          {/* Processing Status */}
          {isProcessing && processingStatus && (
            <Paper sx={{ p: 3, mt: 3, backgroundColor: 'background.default' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Processing Status
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {getCurrentStep()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {getProgressPercentage()}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={getProgressPercentage()}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Typography variant="caption" sx={{ color: 'text.primary', display: 'block' }}>
                💡 You can close this modal - processing will continue in the background and the case will appear in the grid when complete.
              </Typography>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={handleClose}
            sx={{ 
              color: '#FFFFFF',
              textTransform: 'none',
              backgroundColor: 'error.main',
              '&:hover': {
                backgroundColor: 'error.main',
              }
            }}
          >
            {isProcessing ? 'Close (Continue Processing)' : 'Cancel'}
          </Button>
          
          {!isProcessing && (
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: 'primary.main',
                color: 'text.primary',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 140,
                '&:hover': {
                  backgroundColor: 'primary.dark',
                }
              }}
            >
              Start Processing
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};